// ─── Beetsol SearchIntelligence (v2) ─────────────────────────────────────────
// New file: src/components/Insights/SearchIntelligence.jsx
//
// Route: /insights/search-intelligence
// Sidebar: Insights → Search Intelligence
//
// Changes from v1:
// — Filter persistence via URL params (audience, period, cohort from dashboard)
// — Date range options match spec: 7d / 30d / 90d
// — Cohort filter added
//
// API: GET /api/insights/failed-searches?dateRange=7&audience=all&cohort=all&sort=attempts&order=desc&page=1&limit=4
//   Returns: { results: [{ query, attempts, people: { internal, external }, asked_more_than_once, last_asked }],
//              searches, questions, total, page, limit }
//   Searches with no answer, one row per question (N36 grouping), one page at a time (N5 pts 5, 10).
// Searches with an answer: /failed-searches/successful, the same filters, page and limit; returns { results: [{ query,
//   attempts, content_title }], total } (N5 pts 12, 12.1).
// ─────────────────────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// ⚠️ DATA WIRING IS PROVISIONAL — see docs/INSIGHTS_PROVISIONAL.md
// Design final. Data-fetch (search "SEAM-5") wired to insightsService from
// scratch. If real wired Insights files exist, prefer theirs + apply this design.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { tokens } from '../../theme';
import { insightsService } from '../../services/api';
import { useAuthorContentNavigation } from '../../hooks/useAuthorContentNavigation';
import { useInsightsAdminRole } from '../../hooks/useInsightsAdminRole';
import EmptyState from '../common/EmptyState';
import InsightsFilterLine from './InsightsFilterLine';
import InsightsPager from './InsightsPager';

const T = tokens;

// Removed 2 Nov 2026 (N5 pt 1): the page's own audience and period pills (FilterPill). The filters come from the
// dashboard and are not repeated: the page shows them in one read-only line (InsightsFilterLine).

// Removed 12 Oct 2026 (N5 pt 7): the Export CSV / Export PDF buttons. The page is view-only in v1; downloads return
// on the Data Infra tab (DI2, DI3).

// Removed 12 Oct 2026 (N5 pt 11): the audience dot, used only by the Repeat failures table.

// Removed 12 Oct 2026 (N5 pt 10): the Source badge column; the server never sent a source, so it read Platform on
// every row.

// A header that re-sorts its table (N5 pt 6): the first click sorts by it, highest first; the next flips the order.
const SortTh = ({ children, right, active, order, onClick, colSpan, rowSpan }) => (
  <th colSpan={colSpan} rowSpan={rowSpan} onClick={onClick} style={{ fontSize: '11px', color: active ? T.color.textPrimary : T.color.textSecondary, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '9px 18px', textAlign: right ? 'right' : 'left', fontWeight: 400, borderBottom: `0.5px solid ${T.color.borderTertiary}`, fontFamily: T.font.family, background: T.color.bgPrimary, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
    {children}{active ? (order === 'asc' ? ' ↑' : ' ↓') : ''}
  </th>
);

const lastAskedText = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');

// 'Add content', the one label for this action everywhere (N5 pt 10, K3 pt 12): it switches the admin to Author (K17)
// and opens the add-content screen with the question as the title, marked prioritised (K21.2). Removed 12 Oct 2026
// (N5 pt 10): the label 'Draft module →'.
// The theme's one outlined button (K2.3 pt 15). Removed 2 Nov 2026 (K2.3 pt 15): its own light grey border and grey
// text on a clickable box.
const AddContentBtn = ({ term, navigateToContent }) => (
  <Button variant="outlined" size="small" onClick={() => navigateToContent(null, {
    prefill: term, is_prioritized: true,
    // Add content's way back returns here, with this page's filters (K21.2 pt 20.1.a).
    from: { label: 'Search Intelligence', path: window.location.pathname + window.location.search },
  }).catch(err => console.error('Unable to open content form:', err))} sx={{ whiteSpace: 'nowrap' }}>
    Add content
  </Button>
);

// Removed 12 Oct 2026 (N5 pt 12): the Stars rating cell; ratings moved to Content Health (N5 pt 17).

const Th = ({ children, right, colSpan, rowSpan }) => (
  <th colSpan={colSpan} rowSpan={rowSpan} style={{ fontSize: '11px', color: T.color.textSecondary, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '9px 18px', textAlign: right ? 'right' : 'left', fontWeight: 400, borderBottom: `0.5px solid ${T.color.borderTertiary}`, fontFamily: T.font.family, background: T.color.bgPrimary }}>
    {children}
  </th>
);

const Td = ({ children, right, muted }) => (
  <td style={{ fontSize: '13px', color: muted ? T.color.textSecondary : T.color.textPrimary, padding: '11px 18px', textAlign: right ? 'right' : 'left', borderBottom: `0.5px solid ${T.color.borderTertiary}`, fontFamily: T.font.family }}>
    {children}
  </td>
);

// The shared EmptyState with a sentence on its own (N5 pts 8, 13), spaced as the table's cells; card adds the sections'
// frame, for the page's sentence.
// tone="neutral" for the all-clear sentence, which stays grey (K2.3 pt 14.1).
const Empty = ({ message, card, tone }) => (
  <Box sx={card ? { background: T.color.bgPrimary, border: `0.5px solid ${T.color.borderTertiary}`, borderRadius: T.radius.lg, overflow: 'hidden', padding: '14px 18px' } : { padding: '14px 18px' }}>
    <EmptyState framed={false} title={null} message={message} tone={tone} />
  </Box>
);

// Removed 2 Nov 2026 (N5 pt 4): this page's own copy of the pager; the Insights pages share one (InsightsPager).

const SearchIntelligence = () => {
  const navigateToContent = useAuthorContentNavigation();
  const roleChecked = useInsightsAdminRole();
  const [searchParams] = useSearchParams();

  // The dashboard's audience, period and cohort, read-only here (N5 pt 1).
  const audience = searchParams.get('audience') || 'all';
  const period   = searchParams.get('period')   || '7d';
  const cohort   = searchParams.get('cohort')   || 'all';
  // Each table has its own page from the server (N5 pt 5); Searches with no answer also has its order (N5 pts 6, 10).
  const [failed,     setFailed]     = useState(null); // { results, searches, questions, total }
  const [failedPage, setFailedPage] = useState(1);
  const [failedSort, setFailedSort] = useState({ by: 'attempts', order: 'desc' });
  const [answered,     setAnswered]     = useState(null); // { results, total }
  const [answeredPage, setAnsweredPage] = useState(1);

  const PER_PAGE = 4;

  // Removed 2 Nov 2026 (N5 pt 1): writing the page's own filter choice back to the address; it has no filter control.

  // Searches with an answer, one page at a time (N5 pts 5, 12). The period as dateRange 7, 30 or 90, which the server
  // reads (N5 pt 9, K3 pt 2). Removed 11 Oct 2026 (N5 pt 9): sending 'period', which the server ignores, so the page
  // always showed 7 days. Removed 12 Oct 2026 (N5 pts 5, 12): fetching the first page once with no further pages, and
  // reading most_clicked, which the server never sent, so Most clicked read a dash on every row.
  useEffect(() => { setAnsweredPage(1); }, [period, audience, cohort]);
  useEffect(() => {
    if (!roleChecked) return;
    let cancelled = false;
    insightsService.getSuccessfulSearches({ dateRange: parseInt(period, 10) || 7, audience, cohort, page: answeredPage, limit: PER_PAGE })
      .then((r) => { if (!cancelled) setAnswered(r && r.data && r.data.success ? r.data.data : null); })
      .catch((err) => { if (!cancelled) console.error(err); });
    return () => { cancelled = true; };
  }, [period, audience, cohort, answeredPage, roleChecked]);

  // A new filter or order starts the no-answer list again from its first page.
  useEffect(() => { setFailedPage(1); }, [period, audience, cohort, failedSort]);

  // Searches with no answer, one page at a time in the chosen order (N5 pts 5, 6, 10). Removed 12 Oct 2026 (N5 pt 5):
  // slicing the first fetched page in the browser, so page 2 was empty while the count said there were more.
  useEffect(() => {
    if (!roleChecked) return;
    let cancelled = false;
    insightsService.getFailedSearches({
      dateRange: parseInt(period, 10) || 7, audience, cohort,
      sort: failedSort.by, order: failedSort.order, page: failedPage, limit: PER_PAGE,
    })
      .then((r) => { if (!cancelled) setFailed(r && r.data && r.data.success ? r.data.data : null); })
      .catch((err) => { if (!cancelled) console.error(err); });
    return () => { cancelled = true; };
  }, [period, audience, cohort, failedSort, failedPage, roleChecked]);

  const sortFailed = (by) => setFailedSort((cur) => (cur.by === by ? { by, order: cur.order === 'desc' ? 'asc' : 'desc' } : { by, order: 'desc' }));
  // 'Sorted by' names the column (N5 pt 10.1). Removed 2 Nov 2026 (N5 pt 10.1): the People sort and its label.
  const SORT_LABELS = { attempts: 'Attempts', external: 'External', internal: 'Internal', last_asked: 'Last asked' };
  // People per audience: an External and an Internal column when the audience is All, otherwise the selected audience's
  // only (N5 pt 10, as on Content Health); each column shows only its own name and sorts on its own, with no 'People'
  // heading above them (N5 pt 10.1). The audience is plain text, no dot (N10).
  const showExt = audience !== 'internal';
  const showInt = audience !== 'external';
  // Nothing to list (N5 pts 8, 13), once both lists have loaded: with no searches in the period, one sentence instead
  // of both sections; with searches but none in a section, that section's sentence instead of its table, count and
  // order. A search has an answer or has none (K6.1), so both lists empty means no searches.
  const loaded = !!(failed && answered);
  const noFailed = loaded && Number(failed.total) === 0;
  const noAnswered = loaded && Number(answered.total) === 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ marginBottom: '24px' }}>
        <Typography sx={{ fontSize: T.font.size.xl, fontWeight: T.font.weight.medium, color: T.color.textPrimary, letterSpacing: '-0.02em', fontFamily: T.font.family }}>
          Search Intelligence
        </Typography>
        <InsightsFilterLine audience={audience} period={period} cohort={cohort} />
      </Box>

      {noFailed && noAnswered ? (
        <Empty card message="No searches in this period." />
      ) : (
      <>
      {/* Searches with no answer (N5 pt 10): one row per question, grouped as the Unmet Demand card groups them (N36).
          Removed 12 Oct 2026 (N5 pt 10): the title 'Failed searches' with 'N terms', the subtitle 'No content matched
          these queries', and the Search term, Audience and Source columns. */}
      <Box sx={{ background: T.color.bgPrimary, border: `0.5px solid ${T.color.borderTertiary}`, borderRadius: T.radius.lg, marginBottom: '10px', overflow: 'hidden', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `0.5px solid ${T.color.borderTertiary}`, flexWrap: 'wrap', gap: '6px' }}>
          <Box>
            <Box component="span" sx={{ fontSize: T.font.size.md, fontWeight: T.font.weight.medium, color: T.color.textPrimary, fontFamily: T.font.family }}>Searches with no answer</Box>
            {failed && !noFailed && <Box component="span" sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, marginLeft: '8px', fontFamily: T.font.family }}>{failed.searches} {failed.searches === 1 ? 'search' : 'searches'} · {failed.questions} {failed.questions === 1 ? 'question' : 'questions'}</Box>}
          </Box>
          {!noFailed && <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, fontFamily: T.font.family }}>Sorted by {SORT_LABELS[failedSort.by]}</Typography>}
        </Box>
        {noFailed ? (
          <Empty tone="neutral" message="Every search in this period found an answer." />
        ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>Question</Th>
                {showExt && <SortTh right active={failedSort.by === 'external'} order={failedSort.order} onClick={() => sortFailed('external')}>External</SortTh>}
                {showInt && <SortTh right active={failedSort.by === 'internal'} order={failedSort.order} onClick={() => sortFailed('internal')}>Internal</SortTh>}
                <SortTh right active={failedSort.by === 'attempts'} order={failedSort.order} onClick={() => sortFailed('attempts')}>Attempts</SortTh>
                <SortTh right active={failedSort.by === 'last_asked'} order={failedSort.order} onClick={() => sortFailed('last_asked')}>Last asked</SortTh>
                <Th right>Action</Th>
              </tr>
            </thead>
            <tbody>
              {(failed?.results || []).map((row) => (
                <tr key={row.query}>
                  <Td>
                    {row.query}
                    {row.asked_more_than_once > 0 && (
                      <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, fontFamily: T.font.family, marginTop: '2px' }}>
                        Asked more than once by {row.asked_more_than_once} {row.asked_more_than_once === 1 ? 'person' : 'people'}
                      </Typography>
                    )}
                  </Td>
                  {showExt && <Td right>{row.people.external}</Td>}
                  {showInt && <Td right>{row.people.internal}</Td>}
                  <Td right>{row.attempts}</Td>
                  <Td right muted>{lastAskedText(row.last_asked)}</Td>
                  <Td right><AddContentBtn term={row.query} navigateToContent={navigateToContent} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
        )}
        {failed && failed.total > PER_PAGE && <InsightsPager page={failedPage} total={failed.total} perPage={PER_PAGE} onChange={setFailedPage} />}
      </Box>

      {/* Removed 12 Oct 2026 (N5 pt 11): the Repeat failures section. Its signal is the 'Asked more than once by N people'
          line under a question in Searches with no answer (N5 pt 10). */}

      {/* Searches with an answer (N5 pt 12): one row per question, grouped as above (N36); no actions. Removed 12 Oct
          2026 (N5 pt 12): the title 'Successful searches' and its subtitle 'What's working', the Search term column
          and the Avg rating column. */}
      <Box sx={{ background: T.color.bgPrimary, border: `0.5px solid ${T.color.borderTertiary}`, borderRadius: T.radius.lg, overflow: 'hidden', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `0.5px solid ${T.color.borderTertiary}`, flexWrap: 'wrap', gap: '6px' }}>
          <Box component="span" sx={{ fontSize: T.font.size.md, fontWeight: T.font.weight.medium, color: T.color.textPrimary, fontFamily: T.font.family }}>Searches with an answer</Box>
          {!noAnswered && <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, fontFamily: T.font.family }}>Sorted by Attempts</Typography>}
        </Box>
        {noAnswered ? (
          <Empty message="No search in this period found an answer." />
        ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            {/* The third column is the content marked Yes most often for the question, or a dash (N5 pt 12.1, agreed 18 and 20
                Oct). Removed 23 Oct 2026 (N5 pt 12.1): 'Most clicked result'. */}
            <thead><tr><Th>Question</Th><Th right>Attempts</Th><Th right>Content</Th></tr></thead>
            <tbody>
              {(answered?.results || []).map((row) => (
                <tr key={row.query}>
                  <Td>{row.query}</Td>
                  <Td right>{row.attempts}</Td>
                  <Td right muted>{row.content_title || '-'}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
        )}
        {answered && answered.total > PER_PAGE && <InsightsPager page={answeredPage} total={answered.total} perPage={PER_PAGE} onChange={setAnsweredPage} />}
      </Box>
      </>
      )}
    </Box>
  );
};


export default SearchIntelligence;
